import React, { useState, useContext } from 'react';

import View from 'components/View';
import CoachingViewHeader from 'coaching/components/CoachingViewHeader';
import CoachingHelpHeader from 'coaching/components/CoachingHelpFormView/CoachingHelpHeader';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import './styles/CoachingView.scss';

/**
 * The CoachingViewContext is where we store the sidebar state. In order to toggle a sidebar
 * from a child component, you would do the following:
 *
 * ```
 * import { useCoachingViewState } from 'coaching/components/CoachingView';
 *
 * const { setSidebarIsOpen, setSidebar } = useCoachingViewState();
 * <Button onClick={() => {
 *   setSidebar(<span />);
 *   setSidebarIsOpen(true)
 * }}>
 *   Toggle sidebar
 * </Button>
 * ```
 */

type CoachingViewContextType = {
  sidebar: React.ReactElement | undefined;
  setSidebar: (value: React.ReactElement | undefined) => void;
  sidebarIsOpen: boolean;
  setSidebarIsOpen: (value: boolean) => void;
};

const CoachingViewContext = React.createContext<CoachingViewContextType>({
  sidebar: undefined,
  setSidebar: (_value: React.ReactElement | undefined) => {},
  sidebarIsOpen: false,
  setSidebarIsOpen: (_value: boolean) => {},
});

export function useCoachingViewState() {
  return useContext(CoachingViewContext);
}

type Props = {
  children: React.ReactNode;
  helpButtonEventKey?: string;
  headerType?: HeaderType;
  isCoach?: boolean;
};

export default function CoachingView({
  children,
  helpButtonEventKey,
  headerType = 'default',
  isCoach,
}: Props) {
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [sidebar, setSidebar] = useState<React.ReactElement | undefined>(undefined);
  const headerComponentMap: HeaderMap = {
    default: (
      <CoachingViewHeader
        title="Remind Coaching"
        helpButtonEventKey={helpButtonEventKey}
        isCoach={isCoach}
      />
    ),
    help: <CoachingHelpHeader />,
    helpWithLoginButton: <CoachingHelpHeader showLoginButton />,
    none: <React.Fragment />,
  };
  const state: CoachingViewContextType = {
    sidebar,
    sidebarIsOpen,
    setSidebar,
    setSidebarIsOpen,
  };

  // on mobile, if the sidebar is open, we want it to just fill the screen
  if (userAgent.phone() && sidebar && sidebarIsOpen) {
    return (
      <CoachingViewContext.Provider value={state}>
        <div className="CoachingView-sidebar--full">{sidebar}</div>
      </CoachingViewContext.Provider>
    );
  }

  return (
    <CoachingViewContainer context={state} header={headerComponentMap[headerType]}>
      <div className="CoachingView-content">
        <View className="CoachingView-scrollContainer" scroll>
          {children}
        </View>
        {sidebar && sidebarIsOpen && <div className="CoachingView-sidebar">{sidebar}</div>}
      </div>
    </CoachingViewContainer>
  );
}

function CoachingViewContainer({
  children,
  header,
  context,
}: {
  children: React.ReactNode;
  header: React.ReactNode;
  context: CoachingViewContextType;
}) {
  return (
    <CoachingViewContext.Provider value={context}>
      <div className="CoachingView">
        {header}
        {children}
      </div>
    </CoachingViewContext.Provider>
  );
}

export type HeaderType = 'default' | 'help' | 'helpWithLoginButton' | 'none';

type HeaderMap = {
  [K in HeaderType]: React.ReactNode;
};
